import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';

import { Alert, Button, Card, setSubmitError, useAppForm } from '@keboola/design';

import { api } from '../../api';
import { TextLink } from '../../components/TextLink';
import { getApiErrorMessage } from '../../features/auth/errors';
import { signUpFormSchema } from '../../features/auth/schemas';
import { formValidators } from '../../features/auth/validate';

export const Route = createFileRoute('/auth/create-account')({
  component: CreateAccountPage,
  head: () => ({ meta: [{ title: 'Create an account | Keboola Developer Portal' }] }),
});

function CreateAccountPage() {
  // The success screen replaces the form rather than navigating: the confirmation email is the next
  // step, so there is nowhere useful to send the user yet.
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);

  const form = useAppForm({
    defaultValues: { name: '', email: '', password: '', confirmPassword: '' },
    validators: formValidators(signUpFormSchema),
    canSubmitWhenInvalid: true,
    onSubmit: async ({ value, formApi }) => {
      try {
        // `confirmPassword` is a form-only field — it exists to catch a typo and never goes on the wire.
        await api.auth.signUp({ name: value.name, email: value.email, password: value.password });
        setRegisteredEmail(value.email);
      } catch (error) {
        setSubmitError(
          formApi,
          getApiErrorMessage(error, 'There was a problem creating your account.'),
        );
      }
    },
  });

  if (registeredEmail !== null) {
    return (
      <div className="tw:flex tw:flex-col tw:gap-4">
        <Card.Title as="h1">Account created</Card.Title>
        <Alert variant="success" title="Confirm your email address">
          We sent a confirmation link to <strong>{registeredEmail}</strong>. Open it to activate
          your account, then sign in.
        </Alert>
        <Button asChild variant="primary">
          <Link to="/auth/login">Go to sign in</Link>
        </Button>
      </div>
    );
  }

  return (
    <form.AppForm>
      <form.FormBody>
        <Card.Title as="h1">Create an account</Card.Title>
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
          {/* Stated up front because it surprises people: a Keboola platform login does not work here.
              The portal has its own accounts. */}
          A Developer Portal account is separate from your Keboola project login.
        </p>

        <form.AppField name="name">
          {(field) => (
            <field.TextField label="Name" required autoComplete="name" placeholder="Ada Lovelace" />
          )}
        </form.AppField>

        <form.AppField name="email">
          {(field) => (
            <field.TextField
              label="Email"
              type="email"
              required
              autoComplete="email"
              placeholder="you@example.com"
            />
          )}
        </form.AppField>

        <form.AppField name="password">
          {(field) => (
            <field.PasswordField
              label="Password"
              required
              autoComplete="new-password"
              allowPasswordToggle
              help="At least 8 characters, with an uppercase letter, a lowercase letter and a number."
            />
          )}
        </form.AppField>

        <form.AppField name="confirmPassword">
          {(field) => (
            <field.PasswordField
              label="Confirm password"
              required
              autoComplete="new-password"
              allowPasswordToggle
            />
          )}
        </form.AppField>

        <form.SubmitErrorAlert />
        <form.SubmitButton>Create account</form.SubmitButton>

        <p className="tw:m-0 tw:border-t tw:border-border tw:pt-4 tw:text-sm tw:text-muted-foreground">
          Already have an account? <TextLink to="/auth/login">Sign in</TextLink>
        </p>
      </form.FormBody>
    </form.AppForm>
  );
}
